/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { ServicesClientImpl } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreviewCustomData } from '../../../core/types';
import { UniqueIdentifier } from '../../../core/utils';
import { ScreenPreview } from '../../common';
import { Preview } from '../../MyShows/Preview';
import UpcomingListItem from './UpcomingListItem';

import './UpcomingList.scss';

const PREVIEW_DEBOUNCE_DELAY = 300; // ms

interface UpcomingListProps {
    items: UpcomingOffer[] | any;
    selectedIndex?: number;
    ghostMode?: boolean;
}

const UpcomingList: React.FC<UpcomingListProps> = ({ items, selectedIndex, ghostMode }) => {
    const TAG = 'UpcomingList';

    // Memoize service instances to prevent recreation on every render
    const services = useMemo(() => new ServicesClientImpl(), []);
    const newIcon = useMemo(
        () => services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON),
        [services],
    );

    const { recordingsStatusMap } = useTivoSelector((state: RootState) => state.recordingsStatus);
    const upcomingItems = React.useRef<any>(items || []);

    const [previewData, setPreviewData] = useState<TOfferPreviewCustomData | undefined>(undefined);
    const previewDebounceTimer = useRef<NodeJS.Timeout | null>(null);

    const getRecordingStatusIcon = async (item: UpcomingOffer): Promise<string> => {
        const getChannelData = await services.getChannelFromChannelNumber(
            item.channel.channelNumber,
        );

        const { offerId } = item;
        const recordingStatus = recordingsStatusMap.get(`${getChannelData.stationId}${offerId}`);
        return recordingStatus || '';
    };

    const getPreview = useCallback(async () => {
        try {
            const res = await services.fetchOfferPreview(
                items[selectedIndex || 0].offerId,
                'actionScreenUpcomingList',
                UniqueIdentifier.getNextUpcomingNetworkCallId(),
            );

            if (res.networkCallId === UniqueIdentifier.getUpcomingNetworkCallId())
                setPreviewData(res.offerPreview);
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
        }
    }, [items, selectedIndex, services]);

    useEffect(() => {
        // Clear any existing debounce timer
        if (previewDebounceTimer.current) {
            clearTimeout(previewDebounceTimer.current);
        }

        // Debounce the preview fetch
        previewDebounceTimer.current = setTimeout(() => {
            getPreview();
        }, PREVIEW_DEBOUNCE_DELAY);

        return () => {
            if (previewDebounceTimer.current) {
                clearTimeout(previewDebounceTimer.current);
            }
            setPreviewData(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, selectedIndex]);

    useEffect(() => {
        const updateItemsWithRecordingStatus = async () => {
            const updatedItems = await Promise.all(
                items.map(async (item: UpcomingOffer | UpcomingOfferV2) => {
                    const recordingStatus = await getRecordingStatusIcon(item);
                    // If recording status is present, append it to the item, else ignore
                    if (recordingStatus) {
                        return { ...item, recordingStatus };
                    }
                    return item;
                }),
            );

            upcomingItems.current = updatedItems;
        };

        updateItemsWithRecordingStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    return (
        <div className="upcoming-list">
            <div className="upcoming-list-content">
                {upcomingItems?.current.map((item, index) => (
                    <UpcomingListItem
                        key={`${item.id}-${index}`}
                        item={item}
                        index={index}
                        isSelected={index === selectedIndex}
                        ghostMode={ghostMode && index === selectedIndex}
                        newIconUrl={newIcon?.imageUrl}
                    />
                ))}
            </div>
            <ScreenPreview classStyle="upcoming-list-preview">
                {previewData && <Preview offer={previewData} />}
            </ScreenPreview>
        </div>
    );
};

export default UpcomingList;
